import { FC, RefObject, useEffect, useMemo, useState } from 'react'
import { Drawer, Tabs, TabsProps } from 'antd'
import cn from 'classnames'
import Preloader from '@UI/Preloader'
import { useTaskTrackerDrawerResize } from '../../lib/hooks/useTaskTrackerDrawerResize'
import {
  TASK_TRACKER_DRAWER_ACTIVE_TAB_STORAGE_KEY,
  TASK_TRACKER_DRAWER_Z_INDEX,
} from '../../lib/taskTrackerDrawer.constants'
import { TaskTrackerBoardCard, TaskTrackerOnTaskUpdated } from '../../store/taskTracker.types'
import TaskTrackerEditableTitle from '../TaskTrackerEditableTitle/TaskTrackerEditableTitle'
import TaskDescriptionTab from './tabs/TaskDescriptionTab/TaskDescriptionTab'
import TaskInfoTab from './tabs/TaskInfoTab/TaskInfoTab'
import classes from './index.module.css'

export type TaskTrackerTaskDrawerProps = {
  open: boolean
  loading?: boolean
  task: TaskTrackerBoardCard | null
  projectId: number | null
  canEditTask?: boolean
  taskEditDisabledTooltip?: string | null
  canDeleteTask?: boolean
  taskDeleteDisabledTooltip?: string | null
  workspaceRef: RefObject<HTMLElement | null>
  /** По умолчанию `TASK_TRACKER_DRAWER_Z_INDEX`. Из CRM — выше модалки лида. */
  zIndex?: number
  onClose: () => void
  onTaskUpdated?: TaskTrackerOnTaskUpdated
  onTaskDeleted?: (taskId: number) => void
}

const TASK_DRAWER_TABS: TabsProps['items'] = [
  {
    label: 'Инфо',
    key: 'info',
  },
  {
    label: 'Описание',
    key: 'description',
  },
]

const TASK_DRAWER_DEFAULT_TAB_KEY = 'info'

const isTaskDrawerTabKey = (value: unknown): value is string =>
  typeof value === 'string' &&
  TASK_DRAWER_TABS.some((tab) => tab.key === value)

const readStoredTaskDrawerActiveTab = () => {
  if (typeof window === 'undefined') return TASK_DRAWER_DEFAULT_TAB_KEY

  try {
    const raw = window.localStorage.getItem(
      TASK_TRACKER_DRAWER_ACTIVE_TAB_STORAGE_KEY,
    )
    return isTaskDrawerTabKey(raw) ? raw : TASK_DRAWER_DEFAULT_TAB_KEY
  } catch {
    return TASK_DRAWER_DEFAULT_TAB_KEY
  }
}

const writeStoredTaskDrawerActiveTab = (tabKey: string) => {
  if (typeof window === 'undefined') return
  if (!isTaskDrawerTabKey(tabKey)) return

  try {
    window.localStorage.setItem(
      TASK_TRACKER_DRAWER_ACTIVE_TAB_STORAGE_KEY,
      tabKey,
    )
  } catch {
    // ignore quota / privacy mode errors
  }
}

const TaskTrackerTaskDrawer: FC<TaskTrackerTaskDrawerProps> = ({
  open,
  loading = false,
  task,
  projectId,
  canEditTask = true,
  taskEditDisabledTooltip = null,
  canDeleteTask = true,
  taskDeleteDisabledTooltip = null,
  workspaceRef,
  zIndex = TASK_TRACKER_DRAWER_Z_INDEX,
  onClose,
  onTaskUpdated,
  onTaskDeleted,
}) => {
  const [activeTabKey, setActiveTabKey] = useState(readStoredTaskDrawerActiveTab)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [isDescriptionEditing, setIsDescriptionEditing] = useState(false)
  const { width, isResizing, resizeHandleStyle, handleResizeStart } =
    useTaskTrackerDrawerResize(workspaceRef, open)

  const activeTab = useMemo(() => {
    const raw = activeTabKey.trim()
    if (!raw) return TASK_DRAWER_DEFAULT_TAB_KEY

    return TASK_DRAWER_TABS.some((tab) => tab.key === raw)
      ? raw
      : TASK_DRAWER_DEFAULT_TAB_KEY
  }, [activeTabKey])

  const handleTabChange = (tabKey: string) => {
    setActiveTabKey(tabKey)
    writeStoredTaskDrawerActiveTab(tabKey)
  }

  useEffect(() => {
    if (!open) {
      setDescriptionDraft('')
      setIsDescriptionEditing(false)
      return
    }

    if (isDescriptionEditing) {
      return
    }

    setDescriptionDraft(task?.description ?? '')
  }, [isDescriptionEditing, open, task?.description, task?.id])

  return (
    <>
      {open ? (
        <div
          className={cn(classes.resizeHandle, {
            [classes.resizeHandleActive]: isResizing,
          })}
          style={{
            ...resizeHandleStyle,
            zIndex,
          }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Изменить ширину панели задачи"
          onMouseDown={handleResizeStart}
        />
      ) : null}

      <Drawer
      title={
        task ? (
          <div className={classes.drawerTitle}>
            <TaskTrackerEditableTitle
              taskId={task.id}
              title={task.title}
              variant="drawer"
              readOnly={!canEditTask}
              readOnlyReason={taskEditDisabledTooltip}
              onUpdated={canEditTask ? onTaskUpdated : undefined}
            />
          </div>
        ) : (
          'Задача'
        )
      }
      placement="right"
      open={open}
      onClose={onClose}
      width={width}
      zIndex={zIndex}
      rootClassName={cn(classes.drawer, {
        [classes.drawerResizing]: isResizing,
      })}
      mask={false}
      maskClosable={false}
      keyboard={false}
      destroyOnClose
    >
      <div className={classes.tabsWrapper}>
        <Tabs
          items={TASK_DRAWER_TABS}
          activeKey={activeTab}
          onChange={handleTabChange}
          className={classes.customTabs}
        />
      </div>

      <div className={classes.tabContent}>
        {loading ? (
          <div className={classes.loadingState}>
            <Preloader />
          </div>
        ) : (
          <>
            {activeTab === 'info' ? (
              <TaskInfoTab
                key={task?.id}
                task={task}
                projectId={projectId}
                canEditTask={canEditTask}
                taskEditDisabledTooltip={taskEditDisabledTooltip}
                canDeleteTask={canDeleteTask}
                taskDeleteDisabledTooltip={taskDeleteDisabledTooltip}
                onTaskUpdated={onTaskUpdated}
                onTaskDeleted={onTaskDeleted}
              />
            ) : null}

            {activeTab === 'description' ? (
              <TaskDescriptionTab
                key={task?.id}
                taskId={task?.id ?? null}
                value={descriptionDraft}
                canEditTask={canEditTask}
                taskEditDisabledTooltip={taskEditDisabledTooltip}
                onChange={setDescriptionDraft}
                onTaskUpdated={onTaskUpdated}
                onEditingChange={setIsDescriptionEditing}
              />
            ) : null}
          </>
        )}
      </div>
    </Drawer>
    </>
  )
}

export default TaskTrackerTaskDrawer
